import type {
    AttributeErrorTO,
    AttributeErrorTOTypeEnum as AttributeErrorTOTypeEnumType,
    LiveControlWriteRequestTO,
    VehicleAttributeKeyTO as VehicleAttributeKeyTOType,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import {
    AttributeErrorTOTypeEnum,
    VehicleAttributeKeyTO,
} from '@control-center/control-center-client-public-domain-simulation-ts';
import { useMemo, useState } from 'react';

const ATTR_TO_FIELD: Record<VehicleAttributeKeyTOType, keyof LiveControlWriteRequestTO> = {
    [VehicleAttributeKeyTO.SpeedMs]: 'speedMs',
    [VehicleAttributeKeyTO.Position]: 'position',
    [VehicleAttributeKeyTO.AdsState]: 'adsState',
    [VehicleAttributeKeyTO.DrivingGear]: 'drivingGear',
    [VehicleAttributeKeyTO.ParkingBrake]: 'parkingBrake',
    [VehicleAttributeKeyTO.MissionState]: 'missionState',
    [VehicleAttributeKeyTO.MrmState]: 'mrmState',
    [VehicleAttributeKeyTO.Deviations]: 'deviations',
    [VehicleAttributeKeyTO.ConnectivityLatency]: 'connectivityLatency',
};

function deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (typeof a !== 'object' || typeof b !== 'object') return false;
    return JSON.stringify(a) === JSON.stringify(b);
}

function editContentFor<T>(entry: T | undefined): T | undefined {
    if (!entry || typeof entry !== 'object') return;
    const e = entry as Record<string, unknown>;
    return 'value' in e || 'error' in e ? entry : undefined;
}

export interface ResolvedAttribute {
    isPinned: boolean;
    display: { value?: unknown; error?: AttributeErrorTO } | undefined;
    isInError: boolean;
    onValueChange: (patch: { value?: unknown }) => void;
    onErrorToggle: () => void;
    onErrorTypeChange: (errorType: string) => void;
    onPinToggle: (() => void) | undefined;
}

interface Options {
    vin: string;
    pinnedAttributes: VehicleAttributeKeyTOType[];
    canEdit: boolean;
    onWriteAttributes: (vin: string, body: LiveControlWriteRequestTO) => Promise<void>;
    onPinAttribute: (vin: string, attribute: VehicleAttributeKeyTOType) => Promise<void>;
    onUnpinAttribute: (vin: string, attribute: VehicleAttributeKeyTOType) => Promise<void>;
}

const useAttributeEditState = ({
    vin,
    pinnedAttributes,
    canEdit,
    onWriteAttributes,
    onPinAttribute,
    onUnpinAttribute,
}: Options) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [editState, setEditState] = useState<Partial<LiveControlWriteRequestTO>>({});

    const pinned = useMemo(() => new Set<VehicleAttributeKeyTOType>(pinnedAttributes), [pinnedAttributes]);

    const hasDirtyAttributes = Object.keys(editState).length > 0;

    const toggleEditMode = () => {
        setIsEditMode(m => {
            if (m) setEditState({});
            return !m;
        });
    };

    const handleEditChange = (patch: Partial<LiveControlWriteRequestTO>) => {
        setEditState(prev => {
            const next = { ...prev };
            for (const [key, value] of Object.entries(patch) as [keyof LiveControlWriteRequestTO, unknown][]) {
                if (value === undefined) {
                    delete next[key];
                } else {
                    (next as Record<string, unknown>)[key] = value;
                }
            }
            return next;
        });
    };

    const handlePinToggle = async (attribute: VehicleAttributeKeyTOType, currentlyPinned: boolean) => {
        if (isEditMode) {
            const field = ATTR_TO_FIELD[attribute];
            setEditState(prev => ({
                ...prev,
                [field]: {
                    ...(prev[field] ?? {}),
                    pinned: !currentlyPinned,
                },
            }));
        } else if (currentlyPinned) {
            await onUnpinAttribute(vin, attribute);
        } else {
            await onPinAttribute(vin, attribute);
        }
    };

    const handleWriteAttributes = async () => {
        if (!hasDirtyAttributes) return;
        await onWriteAttributes(vin, editState as LiveControlWriteRequestTO);
        setEditState({});
    };

    const resolveAttribute = (
        field: keyof LiveControlWriteRequestTO,
        attrKey: VehicleAttributeKeyTOType,
        serverField: { value?: unknown; error?: AttributeErrorTO } | undefined
    ): ResolvedAttribute => {
        const editEntry = editState[field] as
            | { value?: unknown; error?: AttributeErrorTO; pinned?: boolean }
            | undefined;
        const isPinnedAttr = editEntry?.pinned ?? pinned.has(attrKey);

        const display =
            editContentFor(editEntry) ??
            (serverField ? { value: serverField.value, error: serverField.error } : undefined);
        const isInError = !!display?.error;

        const onValueChange = (patch: { value?: unknown }) => {
            const valueMatchesBaseline = deepEqual(patch.value, serverField?.value);
            const pinMatchesServer = isPinnedAttr === pinned.has(attrKey);
            if (valueMatchesBaseline && pinMatchesServer) {
                handleEditChange({ [field]: undefined } as Partial<LiveControlWriteRequestTO>);
            } else if (valueMatchesBaseline) {
                handleEditChange({ [field]: { pinned: isPinnedAttr } } as Partial<LiveControlWriteRequestTO>);
            } else {
                handleEditChange({
                    [field]: { value: patch.value, pinned: isPinnedAttr },
                } as Partial<LiveControlWriteRequestTO>);
            }
        };

        const onErrorToggle = () => {
            if (isInError) {
                if (!serverField?.error) {
                    handleEditChange({ [field]: undefined } as Partial<LiveControlWriteRequestTO>);
                } else if (serverField?.value !== undefined) {
                    handleEditChange({
                        [field]: { value: serverField.value, pinned: isPinnedAttr },
                    } as Partial<LiveControlWriteRequestTO>);
                } else {
                    handleEditChange({
                        [field]: { value: undefined, pinned: isPinnedAttr },
                    } as Partial<LiveControlWriteRequestTO>);
                }
            } else {
                handleEditChange({
                    [field]: { error: { type: AttributeErrorTOTypeEnum.ErrorUnspecified }, pinned: isPinnedAttr },
                } as Partial<LiveControlWriteRequestTO>);
            }
        };

        const onErrorTypeChange = (errorType: string) => {
            handleEditChange({
                [field]: { error: { type: errorType as AttributeErrorTOTypeEnumType }, pinned: isPinnedAttr },
            } as Partial<LiveControlWriteRequestTO>);
        };

        const onPinToggle = canEdit ? () => handlePinToggle(attrKey, isPinnedAttr) : undefined;

        return {
            isPinned: isPinnedAttr,
            display,
            isInError,
            onValueChange,
            onErrorToggle,
            onErrorTypeChange,
            onPinToggle,
        };
    };

    return {
        isEditMode,
        hasDirtyAttributes,
        toggleEditMode,
        handleWriteAttributes,
        resolveAttribute,
    };
};

export { useAttributeEditState };
